/**
 * StatCardRow — Reusable stat card strip for listing page headers
 *
 * Renders a horizontal row of compact summary cards with:
 * - Circular icon avatar (color-coded by variant)
 * - Label + value on one line (compact)
 * - Optional secondary text to the right of the value
 * - Skeleton loading on values only (card structure always visible)
 * - Consistent height across all cards
 *
 * Usage:
 *   <StatCardRow
 *     loading={loading.contracts}
 *     cards={[
 *       { icon: IconMoney, label: 'Total Commitment', value: '$1.2M', color: COLORS.primary.main },
 *       { icon: IconPayment, label: 'Total Paid', value: '$800K', secondary: '67%', color: COLORS.success.main },
 *     ]}
 *   />
 */

import { ReactNode } from 'react';
import { alpha, Avatar, Box, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { radius } from '../../../utils/designSystem';

export interface StatCardConfig {
	/** MUI icon component */
	icon: SvgIconComponent;
	/** Card label (e.g. "Total Commitment") */
	label: string;
	/** Primary display value (e.g. "$1,200,000.00"). May be a node for richer content (e.g. a mixed-currency tooltip). */
	value: ReactNode;
	/** Optional secondary info shown to the right of the value (e.g. "67%" or "3 contracts") */
	secondary?: string;
	/** Optional local-currency equivalent shown as a sub-line under the value (e.g. "ETB 5,620,000.00") */
	valueLocal?: string;
	/** Accent color for icon background and tint */
	color: string;
	/** Optional extra content below the value (e.g. progress bar) */
	extra?: ReactNode;
}

interface StatCardRowProps {
	/** Array of 2-5 card configs */
	cards: StatCardConfig[];
	/** When true, values show skeleton placeholders */
	loading?: boolean;
}

export const StatCardRow = ({ cards, loading = false }: StatCardRowProps) => {
	// Calculate grid size: distribute evenly across 12 columns
	const gridSize = Math.floor(12 / cards.length) as 2 | 3 | 4 | 6;

	return (
		<Grid container spacing={2} sx={{ mb: 2 }}>
			{cards.map((card, index) => {
				const Icon = card.icon;
				return (
					<Grid item xs={12} sm={6} md={gridSize} key={index}>
						<Paper
							elevation={0}
							sx={{
								bgcolor: '#ffffff',
								border: '1px solid #e5e7eb',
								borderRadius: radius.sm,
								height: '100%',
								p: 2,
							}}>
							<Stack direction="row" alignItems="center" spacing={1.5}>
								{/* Icon */}
								<Avatar
									sx={{
										bgcolor: alpha(card.color, 0.12),
										color: card.color,
										height: 40,
										width: 40,
										flexShrink: 0,
									}}>
									<Icon sx={{ fontSize: 22 }} />
								</Avatar>

								{/* Content */}
								<Box sx={{ flex: 1, minWidth: 0 }}>
									{/* Label */}
									<Typography
										sx={{
											color: '#6b7280',
											fontSize: '1.2rem',
											fontWeight: 500,
											lineHeight: 1.2,
										}}
										noWrap>
										{card.label}
									</Typography>

									{/* Value row */}
									<Stack direction="row" alignItems="baseline" spacing={1}>
										{loading ? (
											<Skeleton variant="text" width={100} sx={{ fontSize: '1.6rem' }} />
										) : (
											<Typography
												sx={{
													color: '#111827',
													fontSize: '1.6rem',
													fontWeight: 700,
													lineHeight: 1.3,
												}}
												noWrap>
												{card.value}
											</Typography>
										)}

										{card.secondary && !loading && (
											<Typography
												sx={{
													color: '#6b7280',
													fontSize: '1.15rem',
													fontWeight: 500,
													whiteSpace: 'nowrap',
												}}>
												{card.secondary}
											</Typography>
										)}
									</Stack>

									{/* Local-currency equivalent sub-line */}
									{card.valueLocal && !loading && (
										<Typography
											sx={{
												color: '#6b7280',
												fontSize: '1.25rem',
												fontWeight: 500,
												lineHeight: 1.2,
												mt: 0.25,
											}}
											noWrap>
											{card.valueLocal}
										</Typography>
									)}

									{/* Optional extra content */}
									{card.extra && !loading && <Box sx={{ mt: 0.5 }}>{card.extra}</Box>}
								</Box>
							</Stack>
						</Paper>
					</Grid>
				);
			})}
		</Grid>
	);
};
